/**
 * SupplierRegistry — UEIM-02 Supplier Registry management
 * Route: /enterprise/suppliers (capability: supplier.read)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { sourcingApi, moodboardApi } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Search, CheckCircle, XCircle, Archive, Edit, ChevronLeft, Tag } from "lucide-react";

const STATUS_COLORS = {
  DRAFT:      "bg-slate-100 text-slate-700",
  APPROVED:   "bg-emerald-100 text-emerald-700",
  SUSPENDED:  "bg-amber-100 text-amber-700",
  ARCHIVED:   "bg-red-100 text-red-700",
};

const STATUS_LABELS = {
  DRAFT:     "Draft",
  APPROVED:  "Approved",
  SUSPENDED: "Suspended",
  ARCHIVED:  "Archived",
};

function SupplierForm({ supplier, categories, onSave, onClose, canApprove }) {
  const isEdit = !!supplier?.id;
  const [form, setForm] = useState({
    company_name: supplier?.company_name || "",
    display_name: supplier?.display_name || "",
    primary_contact_name: supplier?.primary_contact_name || "",
    email: supplier?.email || "",
    phone: supplier?.phone || "",
    city: supplier?.city || "",
    service_areas: (supplier?.service_areas || []).join(", "),
    gst_ref: supplier?.gst_ref || "",
    notes: supplier?.notes || "",
    service_category_ids: supplier?.service_category_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleCat = (catId) => {
    setForm(f => ({
      ...f,
      service_category_ids: f.service_category_ids.includes(catId)
        ? f.service_category_ids.filter(id => id !== catId)
        : [...f.service_category_ids, catId],
    }));
  };

  const handleSave = async () => {
    if (!form.company_name || !form.primary_contact_name || !form.email) {
      toast.error("Company name, contact name, and email are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        service_areas: form.service_areas.split(",").map(s => s.trim()).filter(Boolean),
      };
      delete payload.service_areas_str;
      const res = isEdit
        ? await sourcingApi.updateSupplier(supplier.id, payload)
        : await sourcingApi.createSupplier(payload);
      toast.success(isEdit ? "Supplier updated" : "Supplier created");
      onSave(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save supplier");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Company Name *</Label>
          <Input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} placeholder="Acme Decorators Pvt Ltd" data-testid="supplier-company-name" />
        </div>
        <div>
          <Label>Display Name</Label>
          <Input value={form.display_name} onChange={e => setForm(f => ({...f, display_name: e.target.value}))} placeholder="Acme Decorators" />
        </div>
        <div>
          <Label>Primary Contact *</Label>
          <Input value={form.primary_contact_name} onChange={e => setForm(f => ({...f, primary_contact_name: e.target.value}))} placeholder="Rajesh Kumar" data-testid="supplier-contact-name" />
        </div>
        <div>
          <Label>Email *</Label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="rajesh@acme.in" data-testid="supplier-email" />
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 98765 43210" />
        </div>
        <div>
          <Label>City</Label>
          <Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} placeholder="Mumbai" />
        </div>
        <div className="col-span-2">
          <Label>Service Areas (comma separated)</Label>
          <Input value={form.service_areas} onChange={e => setForm(f => ({...f, service_areas: e.target.value}))} placeholder="Mumbai, Pune, Goa" />
        </div>
        <div className="col-span-2">
          <Label>GST Reference</Label>
          <Input value={form.gst_ref} onChange={e => setForm(f => ({...f, gst_ref: e.target.value}))} placeholder="27XXXXX1234X1Z5" />
        </div>
        <div className="col-span-2">
          <Label>Notes</Label>
          <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Optional internal notes" />
        </div>
      </div>

      {/* Service category tags */}
      <div>
        <Label>Service Categories</Label>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="supplier-category-chips">
          {categories.map(cat => (
            <button
              key={cat.id || cat._id}
              onClick={() => toggleCat(cat.id || cat._id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                form.service_category_ids.includes(cat.id || cat._id)
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="supplier-save-btn">
          {saving ? "Saving…" : isEdit ? "Update Supplier" : "Create Supplier"}
        </Button>
      </div>
    </div>
  );
}

export default function SupplierRegistry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);

  const canManage  = hasCapability(user, "supplier.manage");
  const canApprove = hasCapability(user, "supplier.approve");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [supRes, catRes] = await Promise.all([
        sourcingApi.listSuppliers({ status: (filterStatus && filterStatus !== "__all__") ? filterStatus : undefined, service_category_id: (filterCat && filterCat !== "__all__") ? filterCat : undefined, search: search || undefined }),
        moodboardApi.listServiceCategories(),
      ]);
      setSuppliers(supRes.data?.results || []);
      setCategories(catRes.data || []);
    } catch (e) {
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCat, search]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (supplierId, action) => {
    try {
      if (action === "approve") await sourcingApi.approveSupplier(supplierId);
      else if (action === "suspend") await sourcingApi.suspendSupplier(supplierId);
      else if (action === "archive") await sourcingApi.archiveSupplier(supplierId);
      toast.success(`Supplier ${action}d`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} supplier`);
    }
  };

  const getCatNames = (ids) => {
    return ids.map(id => {
      const cat = categories.find(c => (c.id || c._id) === id);
      return cat?.name || id;
    }).join(", ");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/enterprise")} className="text-slate-400 hover:text-slate-700 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Building2 className="w-7 h-7 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Supplier Registry</h1>
              <p className="text-sm text-slate-500 mt-0.5">Approved partners eligible for sourcing invitations</p>
            </div>
          </div>
          {canManage && (
            <Button onClick={() => { setEditSupplier(null); setShowForm(true); }} data-testid="add-supplier-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Supplier
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or contact…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border-0 shadow-none"
              data-testid="supplier-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="supplier-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="SUSPENDED">Suspended</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44" data-testid="supplier-cat-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id || c._id} value={c.id || c._id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Supplier list */}
        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading suppliers…</div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16" data-testid="supplier-empty-state">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No suppliers found</p>
            {canManage && <p className="text-slate-400 text-sm mt-1">Click "Add Supplier" to register your first partner</p>}
          </div>
        ) : (
          <div className="space-y-3" data-testid="supplier-list">
            {suppliers.map(sup => (
              <div key={sup.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-colors" data-testid={`supplier-card-${sup.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-slate-900">{sup.display_name || sup.company_name}</h3>
                      <Badge className={`${STATUS_COLORS[sup.status]} border-0 text-xs`}>
                        {STATUS_LABELS[sup.status] || sup.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{sup.primary_contact_name} · {sup.email}{sup.phone ? ` · ${sup.phone}` : ""}</p>
                    {sup.city && <p className="text-xs text-slate-400">{sup.city}{sup.service_areas?.length ? ` · ${sup.service_areas.join(", ")}` : ""}</p>}
                    {sup.service_category_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {sup.service_category_ids.map(catId => (
                          <span key={catId} className="flex items-center gap-1 bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                            <Tag className="w-3 h-3" />{getCatNames([catId])}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canManage && (
                      <Button size="sm" variant="outline" onClick={() => { setEditSupplier(sup); setShowForm(true); }} data-testid={`edit-supplier-${sup.id}`}>
                        <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    {canApprove && sup.status === "DRAFT" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleAction(sup.id, "approve")} data-testid={`approve-supplier-${sup.id}`}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {canApprove && sup.status === "APPROVED" && (
                      <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => handleAction(sup.id, "suspend")} data-testid={`suspend-supplier-${sup.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Suspend
                      </Button>
                    )}
                    {canApprove && sup.status !== "ARCHIVED" && (
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" onClick={() => handleAction(sup.id, "archive")} data-testid={`archive-supplier-${sup.id}`}>
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editSupplier ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
          </DialogHeader>
          <SupplierForm
            supplier={editSupplier}
            categories={categories}
            canApprove={canApprove}
            onSave={() => { setShowForm(false); load(); }}
            onClose={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
